import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { apiFetch } from './apiBase';
import { fetchMetricDetail } from './reconMetricDetailLoad';
import ExceptionTable from './ExceptionTable';
import ExceptionExportButtons from './ExceptionExportButtons';
import PersonDetailTable from './PersonDetailTable';
import { useExceptionFilters } from './useExceptionFilters';
import { usePersonFilters } from './usePersonFilters';

const PERSON_METRICS = new Set(['inBoth', 'onlyOracle', 'onlyAdp', 'oraclePersons']);

function buildFilterParams(metric, elementApplied, personApplied) {
  if (metric === 'exceptions' || metric === 'matched') {
    return { ...elementApplied };
  }
  if (PERSON_METRICS.has(metric)) {
    const person = personApplied.person || personApplied.q || '';
    return { person, q: personApplied.q || '' };
  }
  return {};
}

export default function ReconMetricDetail({
  metric,
  label,
  onClose,
  hasReconResults = true,
  refreshKey = 0,
}) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [kind, setKind] = useState('element');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [needsRerun, setNeedsRerun] = useState(false);
  const [offset, setOffset] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const limit = 100;

  const isExceptions = metric === 'exceptions' && hasReconResults;
  const isMatched = metric === 'matched' && hasReconResults;
  const isPersonMetric = PERSON_METRICS.has(metric) && hasReconResults;
  const useElementFilters = isExceptions || isMatched;

  const elementFilters = useExceptionFilters({
    enabled: useElementFilters,
    refreshKey: refreshKey + reloadToken,
    metric: isMatched ? 'matched' : 'exceptions',
  });

  const personFilters = usePersonFilters({
    enabled: isPersonMetric,
    refreshKey: refreshKey + reloadToken,
    metric: metric || '',
  });

  const hasActiveFilters = useElementFilters
    ? elementFilters.hasActiveFilters
    : personFilters.hasActiveFilters;

  const filterParams = useMemo(
    () =>
      buildFilterParams(
        metric,
        useElementFilters ? elementFilters.applied : {},
        useElementFilters ? {} : personFilters.applied
      ),
    [metric, useElementFilters, elementFilters.applied, personFilters.applied]
  );

  const loadSeq = useRef(0);

  useEffect(() => {
    setOffset(0);
  }, [filterParams, metric]);

  const load = useCallback(async () => {
    if (!metric) return;
    const seq = ++loadSeq.current;
    setLoading(true);
    setError('');
    try {
      const data = await fetchMetricDetail(metric, {
        limit,
        offset,
        ...filterParams,
      });
      if (seq !== loadSeq.current) return;
      const rawRows = data.rows || [];
      setRows(
        useElementFilters ? elementFilters.refineRows(rawRows) : rawRows
      );
      setTotal(data.total ?? 0);
      setKind(data.kind || 'element');
      setNeedsRerun(!!data.needsRerun);
    } catch (e) {
      if (seq !== loadSeq.current) return;
      const msg = String(e.message || '');
      setError(
        /failed to fetch/i.test(msg)
          ? 'Could not load this list. Refresh the page or try again.'
          : msg || 'Failed to load list'
      );
      setRows([]);
      setTotal(0);
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [
    metric,
    offset,
    filterParams,
    refreshKey,
    reloadToken,
    useElementFilters,
    elementFilters.refineRows,
  ]);

  const refreshDetails = async () => {
    setRefreshing(true);
    setError('');
    try {
      await apiFetch('/api/recon/refresh-details', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      setReloadToken((t) => t + 1);
    } catch (e) {
      setError(e.message);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  if (!metric) return null;
  if (!hasReconResults) return null;

  const showLoaderOnly = loading && rows.length === 0;

  return (
    <div className="card-panel overflow-hidden ring-1 ring-slate-700">
      <div className="section-header flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="section-title">Detail</p>
          <h3 className="text-lg font-semibold text-slate-100">{label}</h3>
          <p className="text-xs text-slate-400">
            {total.toLocaleString()} record{total === 1 ? '' : 's'}
            {kind === 'element'
              ? ' · person + Oracle element + wage type'
              : metric === 'onlyOracle' || metric === 'oraclePersons'
                ? ' · click person for Oracle elements'
                : metric === 'onlyAdp'
                  ? ' · click person for ADP wage types'
                  : ' · click person to expand detail'}
            {hasActiveFilters ? ' · filtered' : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="btn-secondary py-1.5 text-xs"
          aria-label="Close detail"
        >
          <X className="h-4 w-4" />
          Close
        </button>
      </div>

      <div className="p-5">
      {(needsRerun || (metric === 'matched' && total === 0)) && (
        <div className="alert-warning mb-3">
          <p>
            {metric === 'matched'
              ? 'Matched employee/element rows were not saved for this run.'
              : 'Person list was not saved for this run.'}{' '}
            Refresh the detail lists or run payroll recon again.
          </p>
          <button
            type="button"
            onClick={refreshDetails}
            disabled={refreshing || loading}
            className="btn-secondary mt-2 text-sm disabled:opacity-50"
          >
            {refreshing ? 'Refreshing…' : 'Refresh detail lists'}
          </button>
        </div>
      )}

      {error && <p className="alert-error mb-2">{error}</p>}

      {showLoaderOnly ? (
        <div className="flex items-center gap-2 py-8 text-sm text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      ) : (
        <>
          <p className="mb-2 text-xs text-slate-400">
            {loading && <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />}
            Showing {rows.length.toLocaleString()} of {total.toLocaleString()} on this page
          </p>
          {isExceptions && (
            <>
            <ExceptionExportButtons
              filters={elementFilters.applied}
              total={total}
              disabled={loading && rows.length === 0}
              className="mb-3"
            />
            <ExceptionTable
              rows={rows}
              filterOptions={elementFilters.filterOptions}
              filters={elementFilters.filters}
              onFilterChange={elementFilters.updateFilter}
              onApply={elementFilters.applyFilters}
              onClearFilters={elementFilters.clearAllFilters}
              hasActiveFilters={elementFilters.hasActiveFilters}
              hasPendingApply={elementFilters.hasPendingApply}
              loading={loading}
            />
            </>
          )}
          {isMatched && (
            <ExceptionTable
              rows={rows}
              showCategory={false}
              emptyMessage="No matched rows match the current filters."
              filterOptions={elementFilters.filterOptions}
              filters={elementFilters.filters}
              onFilterChange={elementFilters.updateFilter}
              onApply={elementFilters.applyFilters}
              onClearFilters={elementFilters.clearAllFilters}
              hasActiveFilters={elementFilters.hasActiveFilters}
              hasPendingApply={elementFilters.hasPendingApply}
              loading={loading}
            />
          )}
          {isPersonMetric && (
            <PersonDetailTable
              rows={rows}
              metric={metric}
              filters={personFilters.filters}
              onFilterChange={personFilters.updateFilter}
              onApply={personFilters.applyFilters}
              onClearFilters={personFilters.clearAllFilters}
              hasActiveFilters={personFilters.hasActiveFilters}
              hasPendingApply={personFilters.hasPendingApply}
              loading={loading}
            />
          )}
        </>
      )}

      {total > limit && (
        <div className="mt-3 flex items-center justify-between text-sm text-slate-600">
          <span>
            Showing {offset + 1}–{Math.min(offset + limit, total)} of {total.toLocaleString()}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={offset === 0 || loading}
              onClick={() => setOffset((o) => Math.max(0, o - limit))}
              className="btn-secondary px-2 py-1 text-xs disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={offset + limit >= total || loading}
              onClick={() => setOffset((o) => o + limit)}
              className="btn-secondary px-2 py-1 text-xs disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
